<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ComputedRef, ref } from "vue"
import SupportModal from "@core/components/support/SupportModal.vue"
import { useI18n } from "vue-i18n"

// Composables
import { useRedirect } from "@core/composables"

// Helpers
import {
    formatIdSource,
    formatAddress,
    formatDate,
    formatPerson,
    isStringSimilar,
} from "@core/helpers"

// Store
import { useSourceStore } from "@core/stores/source.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useConfigStore } from "@core/stores/config.store"
import { useAuthStore } from "@core/stores/auth.store"

// Constants & components
import { SOURCES_TYPES } from "@core/constants"
import { storeToRefs } from "pinia"
import { useEventBus, useClipboard } from "@vueuse/core"
import {
    InformationCpcu,
    InformationDalkia,
    InformationPCE,
    InformationPDL,
    InformationRegaz,
    InformationElecSavoie,
    InformationEauBordeaux,
    MeterInformation,
} from "@types"

const sourceStore = useSourceStore()
const buildingStore = useBuildingStore()

/** Affiche la modale support (signalement / aide). */
const supportModalVisible = ref(false)
const configStore = useConfigStore()
const authStore = useAuthStore()
const { redirectWrapper } = useRedirect()

const { building, getBuildingIsViewOnly } = storeToRefs(buildingStore)

const { isDefaultScope } = storeToRefs(configStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            idsource: ({ named }: { named: (key: string) => string }) =>
                `N° ${formatIdSource(named("value"), named("idtype"))}`,
            added_at: ({ named }: { named: (key: string) => string }) =>
                `Ajouté le ${named("date") ? formatDate(named("date")) : "Non disponible"}`,
            building_associated: ({
                named,
            }: {
                named: (key: string) => string
            }) => {
                const count = Number(named("nb_buildings"))
                return count > 0
                    ? `Associé à ${count} bâtiment${count > 1 ? "s" : ""}`
                    : "aucun bâtiment associé"
            },
            see_buildings: "Voir les bâtiments associés",
            usages: ({ named }: { named: (key: string) => string }) => {
                const remaining = Number(named("remaining"))

                if (!named("usages")) {
                    return "Aucun usage associé"
                }

                if (remaining > 0) {
                    return `Utilisé pour ${named("usages")} et ${remaining} autre${remaining > 1 ? "s" : ""}`
                }

                return `Utilisé pour ${named("usages")}`
            },
            menu: {
                access_data: "Accèder aux données",
                understand_data: "Comprendre mes données",
                report_problem: "Signaler un problème",
                remove: "Supprimer",
            },
            last_data: {
                pdl: ({ named }: { named: (key: string) => string }) =>
                    `Dernière mise à jour des données: ${named("date") ? formatDate(named("date")) : "Non disponible"}`,
                // Non-PDL : état d’alignement avec l’opérateur (dapi `data_retrieval.update.state`),
                // pas une date de dernière donnée reçue.
                other: ({ named }: { named: (key: string) => string }) =>
                    named("state") === "uptodate"
                        ? "Données synchronisées"
                        : "Données non synchronisées avec l'opérateur",
            },

            tooltip: {
                copy: {
                    label: "Copier le numéro du compteur",
                    notification: "Numéro copié dans le presse-papier",
                },
                edit_name: {
                    label: "Modifier le nom du compteur",
                },
                tag: {
                    pdl: {
                        active: "Le compteur est en service et le consentement est actif",
                        revoked:
                            "Le consentement lié au compteur a été révoqué",
                        outdated:
                            "Le consentement lié au compteur est obsolète",
                        unsigned:
                            "Le consentement lié au compteur n'est pas signé",
                        non_existant: "Le compteur est inexistant",
                        terminated: "Le compteur est résilié",
                    },
                    pce: {
                        active: "Le consentement lié au compteur est actif et le droit d'accès est actif",
                        revoked:
                            "Le consentement lié au compteur a été révoqué",
                        outdated:
                            "Le consentement lié au compteur est obsolète",
                        unsigned:
                            "Le consentement lié au compteur n'est pas signé",
                        a_valider: "Le droit d'accès est à valider",
                        not_declared: "Le droit d'accès n'est pas déclaré",
                    },
                    default: {
                        active: "Le consentement lié au compteur est actif",
                        revoked:
                            "Le consentement lié au compteur a été révoqué",
                        outdated:
                            "Le consentement lié au compteur est obsolète",
                        unsigned:
                            "Le consentement lié au compteur n'est pas signé",
                    },
                },
            },
            tag: {
                type: {
                    elec: "Électricité",
                    gaz: "Gaz",
                    pce: "PCE",
                    pdl: "PDL",
                    cpcu: "CPCU",
                    dalkia: "Dalkia",
                    eaubordeaux: "Eau de Bordeaux",
                    regaz: "ReGaz",
                    elecsavoie: "Électricité Savoie",
                    urban_network: "Réseau urbain",
                },
                revoked: "Révoqué",
                outdated: "Obsolète",
                unsigned: "Consentement non signé",
                active: "Actif",
                consent_active: "Consentement actif",
                to_validate: "À valider",
                not_declared: "Non déclaré",
                terminated: "Résilié",
                non_existant: "Inexistant",
                cdc_activating: "CDC en cours d'activation",
                cdc_activating_tooltip:
                    "Enedis a reçu la demande d'activation de la courbe de charge. Les premières mesures peuvent mettre jusqu'à 3 jours à remonter — c'est normal de ne pas encore voir de données de courbe.",
            },
            on_behalf: "représentée par",
            unnamed: "Sans nom",
            access: "accéder",
            adress_inconsistent:
                "L'adresse du bâtiment et du compteur ne sont pas identiques.",
            adress_consistent:
                "L'adresse du bâtiment et du compteur sont similaires.",
            add: "Ajouter",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<Props>()

// ================================
// STATE
// ================================

const modalEditNameToggle = ref(false)
const modalEditUsagesToggle = ref(false)

const soutirageInjectionMap = {
    C5: "P4",
    C4: "P3",
    C2: "P1",
    P4: "C5",
    P3: "C4",
    P1: "C2",
}

const menuRef = ref()

/** Modal suppression de la source (hors demande de consentement en cours). */
const deleteDialogVisible = ref(false)
/** Modal suppression de la demande de consentement uniquement. */
const deleteConsentRequestDialogVisible = ref(false)

const NON_DELETABLE_SOURCE_TYPES = [
    SOURCES_TYPES.METEOFRANCE,
    SOURCES_TYPES.OPENMETEO,
    SOURCES_TYPES.RTE,
]

// ================================
// COMPUTED
// ================================

/**
 * Récupère la source par son ID
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * Affiche l’action Supprimer : suppression de la source, ou de la demande si une consentrequest est affichée.
 */
const hasOwnerMenu = computed(() => {
    if (getBuildingIsViewOnly.value) return false
    const s = getSourceById.value
    if (!s) return false
    const type = (s.type ?? s.idtype ?? "") as string
    if (NON_DELETABLE_SOURCE_TYPES.includes(type)) return false
    const isCompanyAdminAndHomeys =
        authStore.isCompanyAdmin && authStore.isClientHomeys
    // Suppression réservée aux admin/owner (table des droits DEV-16282 :
    // « Création et suppression de bâtiments et de sources » = ❌ pour Membre
    // et Utilisateur). La branche UC2 « owns » exigeait jusqu'ici seulement la
    // propriété de la source, sans contrôle de rôle → un Utilisateur (UC2)
    // voyait Supprimer.
    const isOwnerNotHomeys =
        (s.rights?.includes?.("owns") ?? false) &&
        !authStore.isClientHomeys &&
        authStore.isCompanyAdmin
    return isCompanyAdminAndHomeys || isOwnerNotHomeys
})

const items = computed(() => {
    if (!getSourceById.value) return []

    const s = getSourceById.value
    const result: {
        label: string
        icon?: string
        command: () => void
    }[] = []

    if (!s.consentrequest) {
        result.push(
            {
                label: t("menu.access_data"),
                command: () => {
                    redirectWrapper("/source/:_id_source/data", {
                        _id_source: s._id_source,
                    })
                },
            },
            {
                label: t("menu.report_problem"),
                command: () => {
                    supportModalVisible.value = true
                },
            }
        )

        const sourceType = s.type

        if (
            (sourceType === "pdl" || sourceType === "pce") &&
            isDefaultScope.value
        ) {
            result.push({
                label: t("menu.understand_data"),
                command: () => {
                    const url =
                        sourceType === "pdl"
                            ? "https://homeys.notion.site/Comprendre-les-donn-es-de-mon-PDL-03156b95af514a31b6de808412be8f3e"
                            : "https://homeys.notion.site/Comprendre-les-donn-es-de-mon-PCE-a9df7b188cdf4f25bf4ad7e0a4b2398f"

                    window.open(url, "_blank")
                },
            })
        }
    }

    if (hasOwnerMenu.value) {
        result.push({
            label: t("menu.remove"),
            icon: "pi pi-trash",
            command: () => {
                if (s.consentrequest) {
                    deleteConsentRequestDialogVisible.value = true
                } else {
                    deleteDialogVisible.value = true
                }
            },
        })
    }

    return result
})

/**
 * Récupère les informations spécifiques au type de la source (pdl, pce, cpcu, regaz, dalkia, elecsavoie, eaubordeaux)
 */
const getInformationByType = computed<MeterInformation>(() => {
    return sourceStore.getInformationByType(props.meterId) as MeterInformation
})

/**
 * Récupère le nom formaté du propriétaire de la source.
 * Si une entreprise est associée, affiche "Entreprise (représentée par Prénom Nom)"
 * Sinon affiche "Prénom Nom" ou "Non disponible" si les deux sont absents.
 */
const getOwner = computed(() => {
    if (!getInformationByType.value?.consentement?.titulaire) {
        return "Non disponible"
    }

    const fullName = formatPerson(
        getInformationByType.value?.consentement.titulaire.user.firstname || "",
        getInformationByType.value?.consentement.titulaire.user.lastname || ""
    )

    if (
        getInformationByType.value?.consentement.titulaire.company?._id_company
    ) {
        return `${getInformationByType.value?.consentement?.titulaire.company.name} (${t("on_behalf")} ${fullName})`
    }

    return fullName || "Non disponible"
})

/**
 * Computed pour formater les informations de la source à afficher
 */
const getFormattedInfoSource = computed(() => {
    // Type pour les champs d'information
    type InfoField = {
        label: string
        icon?: string | null
        command?: () => void
        tooltip_icon?: string | null
        tooltip_text?: string | null
        color?: string | null
    }

    const hasNoConsentRequest = !getSourceById.value?.consentrequest
    const canEditInlineFields =
        hasNoConsentRequest && !getBuildingIsViewOnly.value

    // Initialise le tableau des informations avec le nom de la source
    const sourceInfos = [
        {
            label: getSourceById.value?.name || t("unnamed"),
            icon: canEditInlineFields ? "pi pi-pencil" : undefined,
            command: canEditInlineFields
                ? () => {
                      modalEditNameToggle.value = !modalEditNameToggle.value
                  }
                : undefined,
            tooltip_icon: t("tooltip.edit_name.label"),
            color: "text-cyan-400",
        },
    ] as InfoField[]

    // Ajout de l'adresse
    const addressInfo = {
        label: formatAddress(getSourceById.value?.address) || "Non disponible",
    } as InfoField

    // Si on a un bâtiment on compare les adresses et on ajoute une icone
    if (building.value) {
        addressInfo.tooltip_text = checkAdress()
            ? t("adress_consistent")
            : t("adress_inconsistent")
        addressInfo.icon = checkAdress()
            ? "pi pi-check"
            : "fal fa-circle-exclamation"
        addressInfo.color = checkAdress() ? "text-green-400" : "text-orange-400"
        addressInfo.tooltip_icon = checkAdress()
            ? t("adress_consistent")
            : t("adress_inconsistent")
    }
    sourceInfos.push(addressInfo)
    sourceInfos.push({
        label: getOwner.value,
    })

    // Ajout des usages
    const usages = getInformationByType?.value?.usages || []
    const threeUsages = usages.slice(0, 3).join(", ")
    const remaining = usages.length > 3 ? usages.length - 3 : 0

    sourceInfos.push({
        label: t("usages", {
            usages: usages.length ? threeUsages : null,
            remaining,
        }),
        icon: canEditInlineFields ? "pi pi-pencil" : undefined,
        command: canEditInlineFields
            ? () => {
                  modalEditUsagesToggle.value = !modalEditUsagesToggle.value
              }
            : undefined,
        tooltip_icon: t("add"),
        tooltip_text:
            remaining > 0
                ? usages.slice(2, usages.length - 1).join(", ")
                : null,
        color: "text-cyan-400",
    })

    if (!hasNoConsentRequest) {
        return sourceInfos
    }
    sourceInfos.push({
        label: t("added_at", {
            date: getSourceById.value?.created_at,
        }),
    })

    // Si on a un pdl on affiche la date de mise à jour des données du pdl
    if (getSourceById.value?.type === "pdl") {
        // On récupère la date de la dernière mise à jour des données
        const lastUpdatedDate =
            getSourceById.value?.informations?.pdl?.data_retrieval?.update
                ?.updated_at ?? null
        // Si on a pas de date de mise à jour on regarde si on a une date de fin d'historique
        const lastHistoricalDate =
            getSourceById.value?.informations?.pdl?.data_retrieval?.historical
                ?.state === "finished"
                ? (getSourceById.value?.informations?.pdl?.data_retrieval
                      ?.historical?.created_at ?? null)
                : null

        sourceInfos.push({
            label: t("last_data.pdl", {
                date: lastUpdatedDate || lastHistoricalDate,
            }),
        })
    }
    // Sinon on affiche l'état de la mise à jour des données
    // On passe `data_retrieval.update.state` (string) et pas l'objet `update` entier,
    // sinon la comparaison `named("state") === "uptodate"` est toujours false et on
    // retombe systématiquement sur « Il y a plus de 2 jours ».
    // On lit aussi via `getInformationByType` pour que tous les types non-PDL
    // (pce, regaz, cpcu, dalkia, elecsavoie, eaubordeaux) utilisent bien la clé
    // alignée sur le type de la source, pas un `informations.pce` codé en dur.
    else {
        const informationForType = getInformationByType.value as {
            data_retrieval?: { update?: { state?: string } }
        } | null
        sourceInfos.push({
            label: t("last_data.other", {
                state:
                    informationForType?.data_retrieval?.update?.state ?? null,
            }),
        })
    }

    if (!building.value) {
        sourceInfos.push({
            label: t("building_associated", {
                nb_buildings: getSourceById.value?.buildings?.length || 0,
            }),
            icon:
                getSourceById.value?.buildings &&
                getSourceById.value.buildings.length > 0
                    ? "pi pi-building"
                    : null,
            tooltip_icon: t("see_buildings"),
            command: () => {
                if (getSourceById.value?.buildings?.length === 1) {
                    // on redirige vers la page compteur du bâtiment associé
                    redirectWrapper("/building/:_id_building/compteur", {
                        _id_building: getSourceById.value?.buildings?.[0]?._id,
                    })
                } else {
                    modalBuildingListToggle.value = true
                }
            },
            color: "text-cyan-400",
        })
    }
    checkAdress()
    return sourceInfos
})

type Tag = {
    severity: string
    label: string
    icon?: string
    tooltip?: string
}

/**
 * Demande de consentement en cours (signature / partage / etc.) : pas de badges d’état consentement, le parcours invitation le porte.
 */
const getHasPendingConsentRequest = computed(() =>
    Boolean(getSourceById.value?.consentrequest)
)

/**
 * Récupère les tags affichés pour un PCE
 */
const getPCETags = computed(() => {
    // On récupère les informations spécifiques au PCE
    const informations = getInformationByType.value as InformationPCE

    if (!informations) return []

    const tags: Tag[] = [
        {
            severity: "primary",
            label: t("tag.type.gaz"),
            icon: "fal fa-fire-flame-curved",
        },
        {
            severity: "primary",
            label: t("tag.type.pce"),
            icon: "fal fa-fire-flame-curved",
        },
    ]

    // Si on a la fréquence du compteur on l'ajoute
    if (informations.compteur?.frequence) {
        tags.push({
            severity: "primary",
            label: informations.compteur?.frequence,
            icon: "fal fa-fire-flame-curved",
        })
    }

    if (!getHasPendingConsentRequest.value) {
        // Gestion du tag lié à l'état du consentement et de l'état du compteur
        const combinedState = [
            {
                condition: informations?.consentement?.etat === "revoked",
                severity: "warn",
                label: t("tag.revoked"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.pdl.revoked"),
            },
            {
                condition: informations?.consentement?.etat === "outdated",
                severity: "warn",
                label: t("tag.outdated"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.pdl.outdated"),
            },
            {
                condition:
                    informations?.consentement?.etat === "unsigned" ||
                    informations?.consentement?.etat === "unknown",
                severity: "warn",
                label: t("tag.unsigned"),
                icon: "pi pi-file-edit",
                tooltip: t("tooltip.tag.pdl.unsigned"),
            },
            {
                condition: informations?.droit_access?.etat === "A valider",
                severity: "warn",
                label: t("tag.to_validate"),
                icon: "pi pi-exclamation-triangle",
                tooltip: t("tooltip.tag.pce.a_valider"),
            },
            {
                condition: !["A valider", "Active"].includes(
                    informations?.droit_access?.etat || ""
                ),
                severity: "warn",
                label: t("tag.not_declared"),
                icon: "pi pi-times",
                tooltip: t("tooltip.tag.pce.not_declared"),
            },
            {
                condition:
                    informations?.droit_access?.etat === "Active" &&
                    informations?.consentement?.etat === "active",
                severity: "success",
                label: t("tag.active"),
                icon: "pi pi-check-circle",
                tooltip: t("tooltip.tag.pce.active"),
            },
        ]
        combinedState.some(({ condition, severity, label, icon, tooltip }) => {
            if (condition) {
                tags.push({ severity, label, icon, tooltip })
                return true // Stop iteration after adding the first valid tag
            }
            return false
        })
    }

    return tags
})

/**
 * Récupère les tags affichés pour un PDL
 */
const getPDLTags = computed(() => {
    const informations = getInformationByType.value as InformationPDL

    if (!informations) return []

    const tags: Tag[] = [
        {
            severity: "primary",
            label: t("tag.type.elec"),
            icon: "pi pi-bolt",
        },
        {
            severity: "primary",
            label: t("tag.type.pdl"),
            icon: "pi pi-bolt",
        },
    ]

    if (
        informations?.compteur?.segment &&
        informations?.data?.soutirage?.has_soutirage
    ) {
        tags.push({
            severity: "primary",
            label: informations?.compteur?.segment,
            icon: "pi pi-bolt",
        })
    }

    // Ajout du segment d'injection si on en a un et si le compteur est en injection
    // Utilisation dans le cas où on a un C5 qui a de l'injection afin d'afficher le segment P4 à côté.
    // Si on a déjà le segment P4, on ne l'affiche pas deux fois
    if (
        informations?.compteur?.segment &&
        informations?.data?.injection?.has_injection
    ) {
        const label =
            informations?.compteur?.segment &&
            Object.prototype.hasOwnProperty.call(
                soutirageInjectionMap,
                informations.compteur.segment
            )
                ? soutirageInjectionMap[
                      informations.compteur
                          .segment as keyof typeof soutirageInjectionMap
                  ]
                : ""

        // Si on a un label on l'ajoute. Sinon on ne met rien
        if (label) {
            tags.push({
                severity: "primary",
                label,
                icon: "pi pi-bolt",
            })
        }
    }

    if (!getHasPendingConsentRequest.value) {
        // Gestion du tag lié à l'état du consentement et de l'état du compteur
        const combinedState = [
            {
                condition: informations?.consentement?.etat === "revoked",
                severity: "warn",
                label: t("tag.revoked"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.pdl.revoked"),
            },
            {
                condition: informations?.consentement?.etat === "outdated",
                severity: "warn",
                label: t("tag.outdated"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.pdl.outdated"),
            },
            {
                condition:
                    informations?.consentement?.etat === "unsigned" ||
                    informations?.consentement?.etat === "unknown",
                severity: "warn",
                label: t("tag.unsigned"),
                icon: "pi pi-file-edit",
                tooltip: t("tooltip.tag.pdl.unsigned"),
            },
            {
                condition: informations?.etat === "resilie",
                severity: "warn",
                label: t("tag.terminated"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.pdl.terminated"),
            },
            {
                condition: informations?.etat === "inexistant",
                severity: "warn",
                label: t("tag.non_existant"),
                icon: "pi pi-exclamation-triangle",
                tooltip: t("tooltip.tag.pdl.non_existant"),
            },
            {
                condition:
                    informations?.etat === "en_service" &&
                    informations?.consentement?.etat === "active",
                severity: "success",
                label: t("tag.active"),
                icon: "pi pi-check-circle",
                tooltip: t("tooltip.tag.pdl.active"),
            },
        ]
        combinedState.some(({ condition, severity, label, icon, tooltip }) => {
            if (condition) {
                tags.push({ severity, label, icon, tooltip })
                return true // Stop iteration after adding the first valid tag
            }
            return false
        })

        if (informations?.data_retrieval?.historical?.state === "delayed") {
            tags.push({
                severity: "info",
                label: t("tag.cdc_activating"),
                icon: "pi pi-clock",
                tooltip: t("tag.cdc_activating_tooltip"),
            })
        }
    }

    return tags
})

/**
 * Récupère les tags affichés pour un CPCU
 */
const getCPCUTags = computed(() => {
    const informations = getInformationByType.value as InformationCpcu

    if (!informations) return []

    const tags = [
        {
            severity: "primary",
            label: t("tag.type.urban_network"),
            icon: "fal fa-heat",
        },
        {
            severity: "primary",
            label: t("tag.type.cpcu"),
            icon: "fal fa-heat",
        },
    ]

    if (!getHasPendingConsentRequest.value) {
        addConsentTags(tags, informations?.consentement?.etat || "unknown")
    }

    return tags
})

/**
 * Récupère les tags affichés pour un Dalkia
 */
const getDalkiaTags = computed(() => {
    const informations = getInformationByType.value as InformationDalkia

    if (!informations) return []

    const tags = [
        {
            severity: "primary",
            label: t("tag.type.urban_network"),
            icon: "fal fa-heat",
        },
        {
            severity: "primary",
            label: t("tag.type.dalkia"),
            icon: "fal fa-heat",
        },
    ]

    if (!getHasPendingConsentRequest.value) {
        addConsentTags(tags, informations?.consentement?.etat || "unknown")
    }

    return tags
})

/**
 * Récupère les tags affichés pour un compteur Eau de Bordeaux
 */
const getEauBordeauxTags = computed(() => {
    const informations = getInformationByType.value as InformationEauBordeaux

    if (!informations) return []

    const tags = [
        {
            severity: "primary",
            label: t("tag.type.eaubordeaux"),
            icon: "fal fa-heat",
        },
    ]

    if (!getHasPendingConsentRequest.value) {
        addConsentTags(tags, informations?.consentement?.etat || "unknown")
    }

    return tags
})
/**
 * Récupère les tags affichés pour un compteur électrique Savoie
 */
const getElecSavoieTags = computed(() => {
    const informations = getInformationByType.value as InformationElecSavoie

    if (!informations) return []

    const tags = [
        {
            severity: "primary",
            label: t("tag.type.elec"),
            icon: "pi pi-bolt",
        },
        {
            severity: "primary",
            label: t("tag.type.elecsavoie"),
            icon: "pi pi-bolt",
        },
    ]

    if (informations?.compteur?.segment) {
        tags.push({
            severity: "primary",
            label: informations?.compteur?.segment,
            icon: "pi pi-bolt",
        })
    }

    if (!getHasPendingConsentRequest.value) {
        addConsentTags(tags, informations?.consentement?.etat || "unknown")
    }

    return tags
})

/**
 * Récupère les tags affichés pour un CPCU
 */
const getRegazTags = computed(() => {
    const informations = getInformationByType.value as InformationRegaz

    if (!informations) return []

    const tags = [
        {
            severity: "primary",
            label: t("tag.type.gaz"),
            icon: "fal fa-heat",
        },
        {
            severity: "primary",
            label: t("tag.type.regaz"),
            icon: "fal fa-heat",
        },
    ]

    if (!getHasPendingConsentRequest.value) {
        addConsentTags(tags, informations?.consentement?.etat || "unknown")
    }

    return tags
})

/**
 * Ajoute les tags liés au consentement dans le tableau de tags passé en paramètre
 * @param tags - tableau de tags à modifier
 * @param state - état du consentement
 */
function addConsentTags(tags: Tag[], state: string) {
    switch (state) {
        case "revoked":
            tags.push({
                severity: "warn",
                label: t("tag.revoked"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.default.revoked"),
            })
            break
        case "outdated":
            tags.push({
                severity: "warn",
                label: t("tag.outdated"),
                icon: "pi pi-ban",
                tooltip: t("tooltip.tag.default.outdated"),
            })
            break
        case "unsigned":
        case "unknown":
            tags.push({
                severity: "warn",
                label: t("tag.unsigned"),
                icon: "pi pi-file-edit",
                tooltip: t("tooltip.tag.default.unsigned"),
            })
            break
        case "active":
            tags.push({
                severity: "success",
                label: t("tag.active"),
                icon: "pi pi-check-circle",
                tooltip: t("tooltip.tag.default.active"),
            })
            break
    }
}

/**
 * Récupère les tags à afficher en fonction du type de la source
 */
const getSourceTags = computed(() => {
    const sourceType = getSourceById.value?.type

    const tagGetters: Record<string, ComputedRef<Tag[]>> = {
        pce: getPCETags,
        pdl: getPDLTags,
        cpcu: getCPCUTags,
        dalkia: getDalkiaTags,
        eaubordeaux: getEauBordeauxTags,
        regaz: getRegazTags,
        elecsavoie: getElecSavoieTags,
    }

    return sourceType && sourceType in tagGetters
        ? tagGetters[sourceType].value
        : []
})

// ================================
// BUSINESS LOGIC
// ================================

/**
 * Vérifie si l'adresse du compteur est similaire à celle du bâtiment
 */
function checkAdress() {
    // Si on n'a pas de bâtiment on considère que c'est ok
    if (!building.value) return true

    // On compare les champs street, zipcode et city
    return (
        isStringSimilar(
            getSourceById.value?.address?.street || "",
            building.value?.address?.street || "",
            0.8
        ) &&
        getSourceById.value?.address?.zipcode ===
            building.value?.address?.zipcode &&
        isStringSimilar(
            getSourceById.value?.address?.city || "",
            building.value?.address?.city || "",
            0.8
        )
    )
}
/**
 * méthode pour ouvrir le menu
 * @param event
 */
const onToggleMenu = (event: Event) => {
    menuRef.value.toggle(event)
}

// ================================
// INTERACTIONS
// ================================
const bus = useEventBus("notification")

/**
 * Composable pour copier dans le presse-papier
 */
const { copy } = useClipboard()

/**
 * Quand on clique sur copier le numéro du compteur.
 * Le numéro est copié dans le presse-papier et une notification est affichée.
 */
function onClickCopyIdSource() {
    copy(getSourceById.value?.idsource || "")

    bus.emit({
        severity: "success",
        title: t("tooltip.copy.notification"),
        life: 3000,
    })
}

// ================================
// Modal de la liste des bâtiments associés
// ================================
/**
 * Etat d'ouverture/fermeture de la modal de la liste des bâtiments associés
 */
const modalBuildingListToggle = ref(false)

/**
 * Indique si la source est associée à plusieurs bâtiments
 */
const hasBuilding = computed(() => {
    return (getSourceById.value?.buildings?.length ?? 0) > 1
})

/**
 * Récupère les bâtiments associés à la source
 */
const getBuildingsBySourceId = computed(() => {
    // Si on n'a pas de bâtiment on retourne un tableau vide
    if (!getSourceById.value?.buildings) return []
    // On map les bâtiments pour ne garder que les champs nécessaires
    return getSourceById.value?.buildings?.map((building) => ({
        _id: building._id,
        name: building.name || t("unnamed"),
        address: building.address,
        niveau: building.niveau,
    }))
})
</script>

<template>
    <div class="meter-card-header flex flex-col gap-4">
        <!-- Ligne avec l'icone, le nom copiable, le bouton de copie et les tags -->
        <div class="flex flex-row items-center gap-4 justify-between">
            <div
                class="flex flex-col md:flex-row gap-2 font-bold items-start md:items-center"
            >
                <!-- icone de la souce -->
                <BaseSourceIcon :type="getSourceById?.type" :size="32" />
                <!-- Nom copiable de la source -->
                <h2
                    @click="onClickCopyIdSource"
                    class="cursor-pointer hover:text-blue-500"
                >
                    {{
                        t("idsource", {
                            value: getSourceById?.idsource,
                            idtype: getSourceById?.type,
                        })
                    }}
                </h2>
                <!-- Bouton pour copier l'id de la source -->
                <BaseButton
                    v-tooltip.top="t('tooltip.copy.label')"
                    variant="text"
                    rounded
                    aria-label="Filter"
                    @click="onClickCopyIdSource"
                    class="p-1 w-8 h-8"
                >
                    <BaseIcon icon="fal fa-clipboard" />
                </BaseButton>
                <!-- Tags la sources  -->
                <div class="flex gap-2">
                    <BaseTag
                        v-for="(tag, index) in getSourceTags"
                        :key="index"
                        :severity="tag.severity"
                        v-tooltip.top="tag.tooltip || null"
                    >
                        <BaseIcon
                            v-if="tag.icon"
                            :icon="tag.icon"
                            size="12px"
                        />
                        <span>{{ tag.label }}</span>
                    </BaseTag>
                </div>
            </div>
            <div v-if="items.length">
                <a @click.prevent="onToggleMenu" class="cursor-pointer mr-6">
                    <BaseIcon icon="far fa-ellipsis" />
                </a>
                <BaseMenu ref="menuRef" :model="items" popup />
            </div>
        </div>
        <!-- Infos de la source: nom, address, owner, usages, d'ate d'ajout, mise à jour des données -->
        <div
            class="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 text-sm"
        >
            <div
                v-for="(field, index) in getFormattedInfoSource"
                class="flex items-center gap-2"
                :key="index"
            >
                <p class="inline-block" v-tooltip="field.tooltip_text">
                    {{ field.label }}
                </p>
                <!-- icone associé -->
                <BaseIcon
                    :class="`${field.color} ${field.command ? 'cursor-pointer' : ''}`"
                    v-if="field.icon"
                    v-tooltip.top="field.tooltip_icon"
                    :icon="field.icon"
                    @click="field.command"
                />
                <!-- séparateur des infos -->
                <span
                    v-if="index < getFormattedInfoSource.length - 1"
                    class="text-cyan-400"
                    >•</span
                >
            </div>
        </div>
        <!-- Modal de modification du nom -->
        <DialogMeterEditName
            v-model:visible="modalEditNameToggle"
            :building-name="getSourceById?.name"
            :meter-id="meterId"
            @update="
                sourceStore.fetchSourceById({
                    _id_source: meterId,
                })
            "
        />
        <!-- Modal d'ajout/suppression des usages -->
        <DialogMeterEditUsage
            v-model:visible="modalEditUsagesToggle"
            :type="getSourceById?.type"
            :usages="getInformationByType?.usages"
            :meter-id="meterId"
            @update="
                sourceStore.fetchSourceById({
                    _id_source: meterId,
                })
            "
        />
        <!-- Dialog de la liste des bâtiments associés -->
        <DialogMeterBuildingList
            v-if="hasBuilding"
            :buildings="getBuildingsBySourceId"
            v-model:visible="modalBuildingListToggle"
        />
        <SourceDeleteDialog
            v-model:visible="deleteDialogVisible"
            :source-id="meterId"
            :source-label="getSourceById?.idsource"
        />
        <ConsentRequestDeleteDialog
            v-model:visible="deleteConsentRequestDialogVisible"
            :source-id="meterId"
            :source-label="getSourceById?.idsource"
        />
        <SupportModal v-model:visible="supportModalVisible" />
    </div>
</template>
